import React, { useId, useMemo, useState } from 'react';
import { FileEarmarkText } from 'react-bootstrap-icons';
import { ReactComponent as UploadIcon } from '../images/box-arrow-in-up.svg';
import axiosInstance from '../api/api';
import { API_ROUTES } from '../config/api-routes';
import { toast } from 'react-toastify';
import './ResumeUpload.css';

interface ResumeUploadProps {
  value?: File | null;
  existingResumeUrl?: string;
  /** Original upload filename when the stored URL is an opaque key (e.g. S3 UUID). */
  existingResumeFileName?: string;
  uploadImmediately?: boolean;
  showLabel?: boolean;
  label?: string;
  showOptionalBadge?: boolean;
  displayMode?: 'form' | 'panel';
  /** Full-width dashed drop zone (onboarding step 3). */
  variant?: 'default' | 'dropzone';
  onFileSelect?: (file: File | null) => void;
  onUploadSuccess?: (fileUrl: string, fileName?: string) => void;
}

const ALLOWED_RESUME_TYPES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
const ALLOWED_RESUME_EXTENSIONS = new Set(['pdf', 'docx']);
const MAX_FILE_SIZE = 8 * 1024 * 1024;

function isAllowedResumeFile(file: File): boolean {
  if (ALLOWED_RESUME_TYPES.has(file.type)) {
    return true;
  }
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  return ALLOWED_RESUME_EXTENSIONS.has(ext);
}

const ResumeUpload: React.FC<ResumeUploadProps> = ({
  value = null,
  existingResumeUrl,
  existingResumeFileName,
  uploadImmediately = false,
  showLabel = true,
  label = 'Upload Resume',
  showOptionalBadge = false,
  displayMode = 'form',
  variant = 'default',
  onFileSelect,
  onUploadSuccess,
}) => {
  const inputId = useId();
  const [selectedFile, setSelectedFile] = useState<File | null>(value);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');

  const activeFile = value ?? selectedFile;
  const hasExistingResume = Boolean(existingResumeUrl);
  const isPanelMode = displayMode === 'panel';
  const isDropzone = variant === 'dropzone';
  /** Server has a resume URL and no new local file selected — primary action opens the file. */
  const openExistingAsPrimary = Boolean(existingResumeUrl) && !activeFile;

  const fileLabel = useMemo(() => {
    if (activeFile?.name) {
      return activeFile.name;
    }

    if (existingResumeFileName?.trim()) {
      return existingResumeFileName.trim();
    }

    if (!existingResumeUrl) {
      return '';
    }

    const segments = existingResumeUrl.split('/');
    const fromUrl = decodeURIComponent(segments[segments.length - 1] || '').split('?')[0];
    if (/^[0-9a-f]{20,}\.[a-z0-9]+$/i.test(fromUrl)) {
      const ext = fromUrl.split('.').pop()?.toLowerCase();
      if (ext === 'pdf') return 'Resume.pdf';
      if (ext === 'docx') return 'Resume.docx';
      return 'Resume';
    }
    return fromUrl || 'Current resume';
  }, [activeFile, existingResumeFileName, existingResumeUrl]);

  const validateFile = (file: File) => {
    if (!isAllowedResumeFile(file)) {
      return 'Invalid file type. Upload PDF or DOCX.';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'File exceeds 8MB limit.';
    }

    return '';
  };

  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('resume', file);

    try {
      const response = await axiosInstance.post(API_ROUTES.AUTH.COMPLETE_PROFILE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedUrl =
        response?.data?.user?.resume || response?.data?.user?.extended_info?.resume;
      if (!uploadedUrl) {
        throw new Error('Resume upload did not return a file URL.');
      }
      setSelectedFile(null);
      onFileSelect?.(null);
      onUploadSuccess?.(uploadedUrl, file.name);
      window.dispatchEvent(new Event('profile-updated'));
      toast.success('Resume uploaded successfully.');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Failed to upload resume.';
      setError(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';

    if (!file) {
      return;
    }

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      toast.error(validationError);
      return;
    }

    setError('');
    setSelectedFile(file);
    onFileSelect?.(file);

    if (uploadImmediately) {
      await uploadFile(file);
    }
  };

  const triggerClassName = [
    'resume-upload-trigger',
    isPanelMode ? 'resume-upload-trigger-panel' : '',
    isDropzone ? 'resume-upload-trigger-dropzone' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const viewLinkClassName = `resume-upload-view-link ${isPanelMode ? 'resume-upload-trigger-panel' : ''}`;

  const primaryButtonText = () => {
    if (isPanelMode) {
      return hasExistingResume ? 'Replace Resume' : 'Add Resume';
    }
    return hasExistingResume ? 'Replace Resume' : 'Upload Resume';
  };

  const panelMetaLine = openExistingAsPrimary
    ? ''
    : !hasExistingResume && !activeFile
      ? 'PDF or DOCX (Max. 8MB)'
      : '';

  return (
    <div
      className={`resume-upload ${displayMode === 'panel' ? 'resume-upload-panel' : 'resume-upload-form'}`}
    >
      {showLabel && (
        <label htmlFor={inputId} className="resume-upload-label">
          {label}
        </label>
      )}

      <input
        id={inputId}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="resume-upload-input"
        onChange={handleFileChange}
        disabled={isUploading}
      />

      {openExistingAsPrimary ? (
        <>
          <a
            href={existingResumeUrl}
            target="_blank"
            rel="noreferrer"
            className={viewLinkClassName}
            title="Open resume"
          >
            <FileEarmarkText size={isPanelMode ? 18 : 16} aria-hidden="true" />
            <span className="resume-upload-filename">{fileLabel}</span>
          </a>
          <label htmlFor={inputId} className="resume-upload-replace" role="button">
            Replace resume
          </label>
        </>
      ) : (
        <label htmlFor={inputId} className={triggerClassName} role="button">
          {isDropzone && !activeFile ? (
            <>
              <span className="resume-upload-dropzone-main">
                <UploadIcon className="resume-upload-icon" aria-hidden="true" />
                <span className="resume-upload-action-text">
                  {isUploading ? 'Uploading…' : primaryButtonText()}
                </span>
              </span>
              {showOptionalBadge && !hasExistingResume && (
                <span className="resume-upload-badge">Optional</span>
              )}
            </>
          ) : (
            <>
              {(isPanelMode && hasExistingResume) || activeFile ? (
                <FileEarmarkText size={isPanelMode ? 18 : 16} aria-hidden="true" />
              ) : (
                <UploadIcon className="resume-upload-icon" aria-hidden="true" />
              )}
              <span className="resume-upload-filename">
                {isUploading ? 'Uploading…' : activeFile ? fileLabel : primaryButtonText()}
              </span>
              {showOptionalBadge && !hasExistingResume && !activeFile && (
                <span className="resume-upload-badge">Optional</span>
              )}
            </>
          )}
        </label>
      )}

      {isPanelMode && panelMetaLine && (
        <p className="resume-upload-meta resume-upload-panel-meta">{panelMetaLine}</p>
      )}
      {!isPanelMode && (
        <>
          {!openExistingAsPrimary && !fileLabel && (
            <p className="resume-upload-meta">PDF or DOCX (Max. 8MB)</p>
          )}
        </>
      )}

      {isUploading && <p className="resume-upload-meta">Uploading resume...</p>}
      {error && <p className="resume-upload-error">{error}</p>}
    </div>
  );
};

export default ResumeUpload;
